/**
 * Chat domain types and backend parsers. Ports of mobile
 * `message_model.dart`, `conversation_model.dart` and `chat_user_model.dart`.
 *
 * Timestamps stay ISO strings (never `Date`): payloads round-trip through
 * IndexedDB and the REST API unchanged, and ISO-8601 sorts lexicographically.
 */

export type MessageStatus = 'sending' | 'sent' | 'delivered' | 'read'

const STATUS_ORDER: MessageStatus[] = ['sending', 'sent', 'delivered', 'read']

export interface ChatUser {
  id: string
  username: string
  displayName: string
  avatarUrl?: string
  isOnline: boolean
}

export interface Conversation {
  id: string
  otherUser: ChatUser
  lastMessage?: string
  /** ISO timestamp of the previewed message. */
  lastMessageAt?: string
  unreadCount: number
  /** ISO timestamp; drives newest-first ordering. */
  updatedAt: string
}

export interface ChatMessage {
  id: string
  conversationId: string
  senderId: string
  content: string
  imageUrl?: string
  isRead: boolean
  clientMessageId?: string
  status: MessageStatus
  sentAt?: string
  deliveredAt?: string
  readAt?: string
  createdAt: string
}

function str(value: unknown): string | undefined {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return undefined
}

function isoDate(value: unknown): string | undefined {
  if (typeof value !== 'string' || value === '') return undefined
  const t = Date.parse(value)
  return Number.isNaN(t) ? undefined : new Date(t).toISOString()
}

/** Mobile `ChatUserModel.fromJson`; null when id/username/displayName are missing. */
export function parseChatUser(value: unknown): ChatUser | null {
  if (!value || typeof value !== 'object') return null
  const j = value as Record<string, unknown>
  const id = str(j.id)
  const username = str(j.username)
  const displayName = str(j.displayName)
  if (id === undefined || username === undefined || displayName === undefined) return null
  return {
    id,
    username,
    displayName,
    avatarUrl: str(j.avatarUrl),
    isOnline: j.isOnline === true,
  }
}

/**
 * Mobile `ChatUserDetail.fromJson`: the single-conversation endpoint
 * pre-resolves the other participant in flat `otherParticipant*` fields.
 */
export function parseChatUserDetail(value: unknown): ChatUser | null {
  if (!value || typeof value !== 'object') return null
  const j = value as Record<string, unknown>
  const id = str(j.otherParticipantId)
  if (id === undefined) return null
  return {
    id,
    username: str(j.otherParticipantUsername) ?? '',
    displayName: str(j.otherParticipantDisplayName) ?? '',
    avatarUrl: str(j.otherParticipantAvatarUrl),
    isOnline: j.isOnline === true,
  }
}

function previewOf(lastMessage: unknown): { preview?: string; at?: string } {
  // Mobile tolerates a double-wrapped `{ message: {...} }` payload.
  const raw = lastMessage as Record<string, unknown> | null
  const msg = (raw != null && typeof raw === 'object' && (raw.message as object) instanceof Object
    ? raw.message
    : lastMessage) as Record<string, unknown> | null
  if (msg == null || typeof msg !== 'object') return {}
  const content = msg.content
  return {
    preview: typeof content === 'string' ? content : undefined,
    at: isoDate(msg.createdAt),
  }
}

/**
 * Mobile `ConversationModel.fromJson`. Null when the required
 * `otherParticipant` map is absent (mobile throws; the list skips the row).
 */
export function parseConversation(value: unknown): Conversation | null {
  if (!value || typeof value !== 'object') return null
  const j = value as Record<string, unknown>
  if (typeof j.id !== 'string') return null
  const otherUser = parseChatUser(j.otherParticipant)
  if (!otherUser) return null
  const { preview, at } = previewOf(j.lastMessage)
  return {
    id: j.id,
    otherUser,
    lastMessage: preview,
    lastMessageAt: at ?? isoDate(j.lastMessageAt),
    unreadCount: typeof j.unreadCount === 'number' ? j.unreadCount : 0,
    updatedAt: isoDate(j.updatedAt) ?? new Date().toISOString(),
  }
}

/** Mobile `ConversationModel.fromDetail` for `GET /api/conversations/{id}`. */
export function parseConversationDetail(value: unknown): Conversation | null {
  if (!value || typeof value !== 'object') return null
  const j = value as Record<string, unknown>
  if (typeof j.id !== 'string') return null
  const otherUser = parseChatUserDetail(j)
  if (!otherUser) return null
  const { preview, at } = previewOf(j.lastMessage)
  const updatedAt = isoDate(j.updatedAt)
  if (!updatedAt) return null
  return {
    id: j.id,
    otherUser,
    lastMessage: preview,
    lastMessageAt: at,
    unreadCount: typeof j.unreadCount === 'number' ? j.unreadCount : 0,
    updatedAt,
  }
}

function parseStatus(value: unknown, isRead: boolean): MessageStatus {
  if (isRead === true) return 'read'
  if (value === 'sending' || value === 'sent' || value === 'delivered' || value === 'read') return value
  return 'sent'
}

/**
 * Mobile `MessageModel.fromJson`: `id` falls back to `messageId`,
 * everything stringifies, `createdAt` defaults to now.
 */
export function parseMessage(value: unknown): ChatMessage | null {
  if (!value || typeof value !== 'object') return null
  const j = value as Record<string, unknown>
  const id = str(j.id) ?? str(j.messageId)
  if (id === undefined) return null
  const isRead = (j.isRead as boolean) === true
  return {
    id,
    conversationId: str(j.conversationId) ?? '',
    senderId: str(j.senderId) ?? '',
    content: str(j.content) ?? '',
    imageUrl: str(j.imageUrl),
    isRead,
    clientMessageId: str(j.clientMessageId),
    status: parseStatus(j.status, isRead),
    sentAt: isoDate(j.sentAt),
    deliveredAt: isoDate(j.deliveredAt),
    readAt: isoDate(j.readAt),
    createdAt: isoDate(j.createdAt) ?? new Date().toISOString(),
  }
}

/** Mobile `MessageModel.advance`: forward-only; reaching `read` sets `isRead`. */
export function advanceMessageStatus(message: ChatMessage, next: MessageStatus): ChatMessage {
  if (STATUS_ORDER.indexOf(next) <= STATUS_ORDER.indexOf(message.status)) return message
  return { ...message, status: next, isRead: next === 'read' ? true : message.isRead }
}

/**
 * Mobile `ChatRoomProvider._mergeMessages`: a `sending` bubble whose
 * `clientMessageId` already arrived authoritatively is dropped; newest-first.
 */
export function mergeMessages(messages: ChatMessage[]): ChatMessage[] {
  const authoritativeClientIds = new Set(
    messages
      .filter((m) => m.status !== 'sending' && m.clientMessageId)
      .map((m) => m.clientMessageId as string),
  )
  const byId = new Map<string, ChatMessage>()
  for (const m of messages) {
    if (m.status === 'sending' && m.clientMessageId && authoritativeClientIds.has(m.clientMessageId)) {
      continue
    }
    byId.set(m.id, m)
  }
  return [...byId.values()].sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0))
}
